import { SplittingGame } from './SplittingGame.js';
import { SplittingRenderer } from './SplittingRenderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { Position } from '../../core/GridHitDetector.js';
import { Direction } from '../../core/GridGeometry.js';
import { BaseInput, InputCallback } from '../../input/BaseInput.js';
import { KeyboardControllable, TraceStep } from '../../input/KeyboardController.js';

const OPPOSITE: Record<Direction, Direction> = { N: 'S', S: 'N', E: 'W', W: 'E' };

export class SplittingInput extends BaseInput<SplittingGame, SplittingRenderer> implements KeyboardControllable {
    private hoveredCell: Position | null = null;
    private gateMode: boolean = false;
    /** Travel direction along the current trace, or null before its first step. */
    private traceDir: Direction | null = null;

    constructor(
        game: SplittingGame,
        renderer: SplittingRenderer,
        canvas: HTMLCanvasElement,
        onUpdate: InputCallback
    ) {
        super(game, renderer, canvas, onUpdate);
        this.setupEventListeners();
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    protected setupEventListeners(): void {
        this.canvas.addEventListener('click', this.handleClick);
        this.canvas.addEventListener('contextmenu', this.handleRightClick);
        this.canvas.addEventListener('mousemove', this.handleMouseMove);
        this.canvas.addEventListener('mouseleave', this.handleMouseLeave);
    }

    protected removeEventListeners(): void {
        this.canvas.removeEventListener('click', this.handleClick);
        this.canvas.removeEventListener('contextmenu', this.handleRightClick);
        this.canvas.removeEventListener('mousemove', this.handleMouseMove);
        this.canvas.removeEventListener('mouseleave', this.handleMouseLeave);
    }

    private handleClick = (event: MouseEvent): void => {
        const { x, y } = this.getCoordinates(event);
        const hit = this.detector.detectCell(x, y);

        if (!hit) return;

        if (this.gateMode && isVortexGame(this.game)) {
            if (this.game.toggleVortexGate(hit)) {
                this.onUpdate();
            }
            return;
        }

        // Clicking a digit toggles the highlight of the route the light takes to
        // reach it; an empty cell places a splitter.
        const digit = this.game.getGrid().getDigitAt(hit);
        if (digit !== null) {
            this.game.toggleSelectedDigit(digit);
        } else {
            this.game.toggleSplitter(hit);
        }
        this.onUpdate();
    };

    private handleRightClick = (event: MouseEvent): void => {
        event.preventDefault();
        if (this.gateMode) return;
        const { x, y } = this.getCoordinates(event);
        const hit = this.detector.detectCell(x, y);

        if (hit) {
            if (this.game.hasSplitterAt(hit)) {
                this.game.toggleSplitter(hit); // Remove splitter
                this.onUpdate();
            }
        }
    };

    private handleMouseMove = (event: MouseEvent): void => {
        const { x, y } = this.getCoordinates(event);
        const hit = this.detector.detectCell(x, y);

        if (hit && (!this.hoveredCell ||
            hit.row !== this.hoveredCell.row ||
            hit.col !== this.hoveredCell.col)) {
            this.hoveredCell = hit;
        }
    };

    private handleMouseLeave = (): void => {
        this.hoveredCell = null;
    };

    // ── Keyboard mode ─────────────────────────────────────────────────
    // Place/delete toggle a splitter on a cell. Splitters are directionless, so
    // the trace (Mirrors/Walls-style) drops one at each turn and clears one on a
    // straight pass-through.

    placeAtCursor(pos: Position): boolean {
        if (this.gateMode) return false;
        // Mirrors the click: a digit toggles its route highlight, an empty cell
        // places a splitter.
        const digit = this.game.getGrid().getDigitAt(pos);
        if (digit !== null) {
            this.game.toggleSelectedDigit(digit);
            return true;
        }
        this.game.toggleSplitter(pos);
        return true;
    }

    deleteAtCursor(pos: Position): boolean {
        if (this.gateMode) return false;
        if (!this.game.hasSplitterAt(pos)) return false;
        this.game.toggleSplitter(pos);
        return true;
    }

    /** Each trace step drops/clears a splitter along the path — show the hint. */
    wantsTraceHint(): boolean {
        return true;
    }

    traceStart(_pos: Position): void {
        this.traceDir = null; // no beam anchor; the first step just sets the direction
    }

    /** Trace step at the pivot the cursor just left: a turn drops a splitter, a
     *  straight pass clears one, a reversal does nothing. */
    traceStep({ from, moveDir }: TraceStep): boolean {
        const changed = this.traceDir !== null && this.applyTraceSplitter(from, this.traceDir, moveDir);
        this.traceDir = moveDir;
        return changed;
    }

    private applyTraceSplitter(pivot: Position, fromDir: Direction, moveDir: Direction): boolean {
        if (this.gateMode) return false;
        if (this.game.getGrid().hasDigitAt(pivot)) return false;

        if (fromDir === moveDir) {
            // Straight through: clear a splitter in the way, if any.
            if (!this.game.hasSplitterAt(pivot)) return false;
            this.game.toggleSplitter(pivot);
            return true;
        }
        if (OPPOSITE[fromDir] === moveDir) return false; // reversal
        // A turn: ensure a splitter sits at the pivot.
        if (this.game.hasSplitterAt(pivot)) return false;
        this.game.toggleSplitter(pivot);
        return true;
    }

    /** `X` goes to the top-left cell, where beams start. */
    cursorOrigin(): Position | null {
        return { row: 0, col: 0 };
    }
}
